import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Moon, CalendarDays, Sun, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useSettings } from '../contexts/SettingsContext';
import { remindersAPI, groupsAPI } from '../utils/api';
import TimePicker from '../components/TimePicker';

// Helper: derive 12h components from settings (supports new fields + legacy string)
const getSettingsDefaultTime = (settings) => {
  if (settings.default_reminder_hour != null) {
    const h24 = Number(settings.default_reminder_hour);
    return { hour: h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24, minute: settings.default_reminder_minute || 0, period: h24 < 12 ? 'AM' : 'PM' };
  }
  const [h, m] = (settings.defaultReminderTime || '09:00').split(':').map(Number);
  return { hour: h === 0 ? 12 : h > 12 ? h - 12 : h, minute: [0, 30].includes(m) ? m : 0, period: h < 12 ? 'AM' : 'PM' };
};
import { hijriToGregorian, getHijriMonthName, jalaliToGregorian, getPersianMonthName, getJalaliMonthLength, gregorianToJalali, getGregorianMonthName } from '../utils/dateHelpers';
import BottomNav from '../components/BottomNav';

const AddReminderPage = () => {
  const navigate = useNavigate();
  const { reminderId } = useParams();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { settings } = useSettings();
  const isEditMode = Boolean(reminderId);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('birthday');
  const [customEventName, setCustomEventName] = useState('');
  // Multi-calendar toggle state — persianAvailable comes from global SettingsContext
  const [enabledCalendars, setEnabledCalendars] = useState({ gregorian: true, hijri: false, persian: false });
  const persianAvailable = settings.persianCalendarEnabled === true;

  // Date states for each calendar
  const [gregorianDay, setGregorianDay] = useState('1');
  const [gregorianMonth, setGregorianMonth] = useState('1');
  const [gregorianYear, setGregorianYear] = useState(new Date().getFullYear().toString());
  const [hijriDay, setHijriDay] = useState('1');
  const [hijriMonth, setHijriMonth] = useState('1');
  const [hijriYear, setHijriYear] = useState('1447');
  const todayJalali = gregorianToJalali(new Date());
  const [persianDay, setPersianDay] = useState('1');
  const [persianMonth, setPersianMonth] = useState('1');
  const [persianYear, setPersianYear] = useState(todayJalali.year.toString());

  const [description, setDescription] = useState('');
  const [repeatFrequency, setRepeatFrequency] = useState('yearly');
  const [isRepeat, setIsRepeat] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('');
  // Multi-select remind before: same day, 1 day before, and/or custom (1–90 days)
  const [remindSameDay, setRemindSameDay] = useState(true);
  const [remindOneDayBefore, setRemindOneDayBefore] = useState(false);
  const [remindCustom, setRemindCustom] = useState(false);
  const [remindCustomDays, setRemindCustomDays] = useState('');
  // Per-reminder custom notification time (stored/sent as 24h; displayed as 12h)
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [reminderHour, setReminderHour] = useState(9);    // 1–12 display
  const [reminderMinute, setReminderMinute] = useState(0); // 0 or 30
  const [reminderPeriod, setReminderPeriod] = useState('AM');
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const submittingRef = useRef(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const grps = await groupsAPI.getAll();
        setGroups(Array.isArray(grps) ? grps : []);

        if (isEditMode) {
          const reminder = await remindersAPI.get(reminderId);
          if (reminder) {
            setCategory(reminder.category);
            if (reminder.category === 'other' || reminder.category === 'custom') {
              setCustomEventName(reminder.title);
            } else {
              setTitle(reminder.title);
            }
            // In edit mode, only enable the calendar type of the existing reminder
            setEnabledCalendars({
              gregorian: reminder.calendarType === 'gregorian',
              hijri: reminder.calendarType === 'hijri',
              persian: reminder.calendarType === 'persian',
            });
            setDescription(reminder.description || '');
            setIsRepeat(reminder.repeatFrequency !== 'once');
            setRepeatFrequency(reminder.repeatFrequency === 'once' ? 'yearly' : (reminder.repeatFrequency || 'yearly'));
            setSelectedGroup(reminder.groupId || '');
            // Load remindBeforeDays — handle both old int and new array
            const rbd = reminder.remindBeforeDays;
            const slotsArr = Array.isArray(rbd) ? rbd : (typeof rbd === 'number' && rbd > 0 ? [rbd] : [0]);
            setRemindSameDay(slotsArr.includes(0));
            setRemindOneDayBefore(slotsArr.includes(1));
            const customVal = slotsArr.find(d => d > 1);
            setRemindCustom(!!customVal);
            setRemindCustomDays(customVal ? String(customVal) : '');
            // Load custom reminder time
            setUseCustomTime(!!reminder.use_custom_time);
            if (reminder.use_custom_time && reminder.reminder_hour != null) {
              const h24 = Number(reminder.reminder_hour);
              setReminderHour(h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24);
              setReminderMinute(reminder.reminder_minute || 0);
              setReminderPeriod(h24 < 12 ? 'AM' : 'PM');
            } else {
              const [dh, dm] = (settings.defaultReminderTime || '09:00').split(':').map(Number);
              setReminderHour(dh === 0 ? 12 : dh > 12 ? dh - 12 : dh);
              setReminderMinute([0, 30].includes(dm) ? dm : 0);
              setReminderPeriod(dh < 12 ? 'AM' : 'PM');
            }

            const reminderDate = new Date(reminder.date);
            if (reminder.calendarType === 'gregorian') {
              setGregorianDay(reminderDate.getDate().toString());
              setGregorianMonth((reminderDate.getMonth() + 1).toString());
              setGregorianYear(reminderDate.getFullYear().toString());
            } else if (reminder.calendarType === 'hijri' && reminder.hijriDate) {
              setHijriDay(reminder.hijriDate.day.toString());
              setHijriMonth(reminder.hijriDate.month.toString());
              setHijriYear(reminder.hijriDate.year.toString());
            } else if (reminder.calendarType === 'persian' && reminder.persianDate) {
              setPersianDay(reminder.persianDate.day.toString());
              setPersianMonth(reminder.persianDate.month.toString());
              setPersianYear(reminder.persianDate.year.toString());
            }
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, reminderId]);

  const toggleCalendar = (cal) => {
    setEnabledCalendars(prev => {
      const activeCount = Object.values(prev).filter(Boolean).length;
      // Don't allow disabling the last active calendar
      if (prev[cal] && activeCount <= 1) return prev;
      return { ...prev, [cal]: !prev[cal] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    setSaveError('');

    try {
      const eventTitle = (category === 'custom' || category === 'other') ? customEventName : title;

      // Explicit validation
      if (!eventTitle || !eventTitle.trim()) {
        setSaveError(t('reminder.title_required'));
        setSaving(false);
        submittingRef.current = false;
        return;
      }

      const noCalendarSelected = !enabledCalendars.gregorian && !enabledCalendars.hijri && !enabledCalendars.persian;
      if (noCalendarSelected) {
        setSaveError(t('reminder.select_calendar'));
        setSaving(false);
        submittingRef.current = false;
        return;
      }
      const defaultTime = settings.defaultReminderTime || '09:00';
      const [hours, minutes] = defaultTime.split(':');
      const effectiveFrequency = isRepeat ? repeatFrequency : 'once';
      const maxCustomDays = repeatFrequency === 'weekly' ? 6 : repeatFrequency === 'monthly' ? 29 : 90;
      const customDaysVal = parseInt(remindCustomDays);
      const baseFields = {
        category,
        description,
        repeatYearly: effectiveFrequency === 'yearly',
        repeatFrequency: effectiveFrequency,
        groupId: selectedGroup || null,
        remindBeforeDays: [
          ...(remindSameDay ? [0] : []),
          ...(remindOneDayBefore ? [1] : []),
          ...(remindCustom && !isNaN(customDaysVal) && customDaysVal >= 1 ? [Math.min(maxCustomDays, customDaysVal)] : []),
        ].filter((v, i, a) => a.indexOf(v) === i) || [0],
        use_custom_time: useCustomTime,
        reminder_hour: useCustomTime ? (() => {
          if (reminderPeriod === 'PM' && reminderHour !== 12) return reminderHour + 12;
          if (reminderPeriod === 'AM' && reminderHour === 12) return 0;
          return reminderHour;
        })() : null,
        reminder_minute: useCustomTime ? reminderMinute : null,
      };

      const remindersToCreate = [];

      if (enabledCalendars.gregorian) {
        const d = new Date(parseInt(gregorianYear), parseInt(gregorianMonth) - 1, parseInt(gregorianDay));
        d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        remindersToCreate.push({ ...baseFields, title: eventTitle, calendarType: 'gregorian', date: d.toISOString() });
      }
      if (enabledCalendars.hijri) {
        const d = hijriToGregorian(parseInt(hijriDay), parseInt(hijriMonth), parseInt(hijriYear));
        d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        remindersToCreate.push({
          ...baseFields, title: eventTitle, calendarType: 'hijri', date: d.toISOString(),
          hijriDate: { day: parseInt(hijriDay), month: parseInt(hijriMonth), year: parseInt(hijriYear) },
        });
      }
      if (enabledCalendars.persian) {
        const d = jalaliToGregorian(parseInt(persianDay), parseInt(persianMonth), parseInt(persianYear));
        d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        remindersToCreate.push({
          ...baseFields, title: eventTitle, calendarType: 'persian', date: d.toISOString(),
          persianDate: { day: parseInt(persianDay), month: parseInt(persianMonth), year: parseInt(persianYear) },
        });
      }

      if (isEditMode) {
        await remindersAPI.update(reminderId, remindersToCreate[0]);
      } else {
        for (const rem of remindersToCreate) {
          await remindersAPI.create(rem);
        }
      }

      navigate('/home');
    } catch (err) {
      console.error('Failed to save reminder:', err);
      setSaveError(err.response?.data?.detail || 'Failed to save reminder. Please try again.');
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };

  const hijriMonths = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: getHijriMonthName(i + 1, lang) }));
  const gregorianMonths = [
    t('month.jan'), t('month.feb'), t('month.mar'), t('month.apr'), t('month.may'), t('month.jun'),
    t('month.jul'), t('month.aug'), t('month.sep'), t('month.oct'), t('month.nov'), t('month.dec')
  ];
  const persianMonths = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: getPersianMonthName(i + 1, lang) }));

  const hijriYears = Array.from({ length: 110 }, (_, i) => 1347 + i);
  const gregorianYears = Array.from({ length: 110 }, (_, i) => new Date().getFullYear() - 100 + i);
  const persianYears = Array.from({ length: 110 }, (_, i) => todayJalali.year - 100 + i);

  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const gregorianDaysInMonth = getDaysInMonth(parseInt(gregorianMonth), parseInt(gregorianYear));
  const persianDaysInMonth = getJalaliMonthLength(parseInt(persianYear), parseInt(persianMonth));

  const enabledCount = Object.values(enabledCalendars).filter(Boolean).length;

  if (loadingData) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-24 flex-1">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <button onClick={() => navigate('/home')} data-testid="back-button"
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform">
              <ArrowLeft size={20} className="text-slate-900" />
            </button>
            <div className="flex-1 flex items-center justify-between">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isEditMode ? t('reminder.edit') : t('reminder.add')}
              </h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('reminder.category')}</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'birthday',    label: t('reminder.birthday') },
                  { key: 'remember',    label: t('reminder.remember') },
                  { key: 'anniversary', label: t('reminder.anniversary') },
                  { key: 'other',       label: t('reminder.category.other') }
                ].map((cat) => (
                  <button key={cat.key} type="button" onClick={() => setCategory(cat.key)} data-testid={`category-${cat.key}`}
                    className={`py-3 rounded-xl font-semibold text-sm transition-all ${
                      category === cat.key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'
                    }`}>
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Event Name */}
            {(category === 'other' || category === 'custom') ? (
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('reminder.event_name')}</label>
                <input type="text" required value={customEventName} onChange={(e) => setCustomEventName(e.target.value.slice(0, 30))}
                  placeholder={t('reminder.event_placeholder')} maxLength={30} data-testid="event-name-input"
                  className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors placeholder:text-slate-400" />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('reminder.person_name')}</label>
                <input type="text" required value={title} onChange={(e) => setTitle(e.target.value.slice(0, 30))}
                  placeholder={
                    category === 'birthday' ? t('reminder.birthday_placeholder') :
                    category === 'anniversary' ? t('reminder.anniversary_placeholder') :
                    t('reminder.remember_placeholder')
                  }
                  maxLength={30}
                  data-testid="person-name-input"
                  className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors placeholder:text-slate-400" />
              </div>
            )}

            {/* Calendar Selection — Multi-Toggle Chips */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('reminder.calendar_type')}</label>
              <div className="flex gap-2">
                {/* Gregorian Chip */}
                <button type="button" data-testid="calendar-toggle-gregorian" onClick={() => toggleCalendar('gregorian')}
                  className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1.5 ${
                    enabledCalendars.gregorian ? 'bg-blue-500 text-white shadow-sm ring-2 ring-blue-500/30' : 'bg-white text-slate-500 border border-slate-200'
                  }`}>
                  {enabledCalendars.gregorian && <Check size={14} strokeWidth={3} />}
                  <CalendarDays size={16} />
                  <span>{t('reminder.gregorian')}</span>
                </button>
                {/* Hijri Chip */}
                <button type="button" data-testid="calendar-toggle-hijri" onClick={() => toggleCalendar('hijri')}
                  className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1.5 ${
                    enabledCalendars.hijri ? 'bg-emerald-500 text-white shadow-sm ring-2 ring-emerald-500/30' : 'bg-white text-slate-500 border border-slate-200'
                  }`}>
                  {enabledCalendars.hijri && <Check size={14} strokeWidth={3} />}
                  <Moon size={16} />
                  <span>{t('reminder.hijri')}</span>
                </button>
                {/* Persian Chip (only if enabled in Settings) */}
                {persianAvailable && (
                  <button type="button" data-testid="calendar-toggle-persian" onClick={() => toggleCalendar('persian')}
                    className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1.5 ${
                      enabledCalendars.persian ? 'bg-orange-500 text-white shadow-sm ring-2 ring-orange-500/30' : 'bg-white text-slate-500 border border-slate-200'
                    }`}>
                    {enabledCalendars.persian && <Check size={14} strokeWidth={3} />}
                    <Sun size={16} />
                    <span>{t('reminder.persian')}</span>
                  </button>
                )}
              </div>
              {enabledCount > 1 && !isEditMode && (
                <p className="text-xs text-slate-400 mt-1.5 text-center">{enabledCount} calendars selected — {enabledCount} reminders will be created</p>
              )}
            </div>

            {/* Date Section — contextual label explains this is the actual event date */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-0.5" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {category === 'birthday' ? t('reminder.date_of_birth') :
                 category === 'remember' ? t('reminder.date_of_passing') :
                 category === 'anniversary' ? t('reminder.anniversary_date') :
                 t('reminder.event_date')}
              </label>
              <p className="text-xs text-slate-400 mb-2">{t('reminder.date_hint')}</p>
            </div>

            {/* Date Pickers — stacked for each enabled calendar */}
            <AnimatePresence mode="sync">
              {/* Gregorian Date Picker */}
              {enabledCalendars.gregorian && (
                <motion.div key="greg-picker" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className={`overflow-hidden ${enabledCount > 1 ? 'bg-blue-50 p-4 rounded-xl' : ''}`}>
                  {enabledCount > 1 && <h3 className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-2 flex items-center gap-1.5"><CalendarDays size={14} />{t('reminder.gregorian_date')}</h3>}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.day')}</label>
                      <select required value={gregorianDay} onChange={(e) => setGregorianDay(e.target.value)}
                        className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-3 py-3 text-slate-900 transition-colors">
                        {Array.from({ length: gregorianDaysInMonth }, (_, i) => i + 1).map((day) => (<option key={day} value={day}>{day}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.month')}</label>
                      <select required value={gregorianMonth} onChange={(e) => {
                        setGregorianMonth(e.target.value);
                        const d = getDaysInMonth(parseInt(e.target.value), parseInt(gregorianYear));
                        if (parseInt(gregorianDay) > d) setGregorianDay(d.toString());
                      }} className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-2 py-3 text-slate-900 transition-colors text-sm">
                        {gregorianMonths.map((month, idx) => (<option key={idx + 1} value={idx + 1}>{month}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.year')}</label>
                      <select required value={gregorianYear} onChange={(e) => {
                        setGregorianYear(e.target.value);
                        const d = getDaysInMonth(parseInt(gregorianMonth), parseInt(e.target.value));
                        if (parseInt(gregorianDay) > d) setGregorianDay(d.toString());
                      }} className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-2 py-3 text-slate-900 transition-colors text-sm">
                        {gregorianYears.map((year) => (<option key={year} value={year}>{year}</option>))}
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Hijri Date Picker */}
              {enabledCalendars.hijri && (
                <motion.div key="hijri-picker" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className={`overflow-hidden ${enabledCount > 1 ? 'bg-emerald-50 p-4 rounded-xl' : ''}`}>
                  {enabledCount > 1 && <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Moon size={14} />{t('reminder.hijri_date')}</h3>}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.day')}</label>
                      <select required value={hijriDay} onChange={(e) => setHijriDay(e.target.value)}
                        className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-3 py-3 text-slate-900 transition-colors">
                        {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (<option key={day} value={day}>{day}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.month')}</label>
                      <select required value={hijriMonth} onChange={(e) => setHijriMonth(e.target.value)}
                        className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-2 py-3 text-slate-900 transition-colors text-sm">
                        {hijriMonths.map((month) => (<option key={month.value} value={month.value}>{month.label}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.year')}</label>
                      <select required value={hijriYear} onChange={(e) => setHijriYear(e.target.value)}
                        className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-2 py-3 text-slate-900 transition-colors text-sm">
                        {hijriYears.map((year) => (<option key={year} value={year}>{year}</option>))}
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Persian Date Picker */}
              {enabledCalendars.persian && (
                <motion.div key="persian-picker" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className={`overflow-hidden ${enabledCount > 1 ? 'bg-orange-50 p-4 rounded-xl' : ''}`}>
                  {enabledCount > 1 && <h3 className="text-xs font-bold text-orange-600 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Sun size={14} />{t('reminder.persian_date')}</h3>}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.day')}</label>
                      <select required value={persianDay} onChange={(e) => setPersianDay(e.target.value)} data-testid="persian-day-select"
                        className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-3 py-3 text-slate-900 transition-colors">
                        {Array.from({ length: persianDaysInMonth }, (_, i) => i + 1).map((day) => (<option key={day} value={day}>{day}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.month')}</label>
                      <select required value={persianMonth} onChange={(e) => {
                        setPersianMonth(e.target.value);
                        const d = getJalaliMonthLength(parseInt(persianYear), parseInt(e.target.value));
                        if (parseInt(persianDay) > d) setPersianDay(d.toString());
                      }} data-testid="persian-month-select"
                        className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-2 py-3 text-slate-900 transition-colors text-sm">
                        {persianMonths.map((month) => (<option key={month.value} value={month.value}>{month.label}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">{t('reminder.year')}</label>
                      <select required value={persianYear} onChange={(e) => {
                        setPersianYear(e.target.value);
                        const d = getJalaliMonthLength(parseInt(e.target.value), parseInt(persianMonth));
                        if (parseInt(persianDay) > d) setPersianDay(d.toString());
                      }} data-testid="persian-year-select"
                        className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-2 py-3 text-slate-900 transition-colors text-sm">
                        {persianYears.map((year) => (<option key={year} value={year}>{year}</option>))}
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {t('reminder.description')} <span className="text-slate-400 font-normal">({t('reminder.optional')})</span>
              </label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 300))}
                placeholder={t('reminder.description_placeholder')} rows={3} maxLength={300} data-testid="description-input"
                className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors placeholder:text-slate-400 resize-none" />
              <p className="text-xs text-slate-400 mt-1 text-right">{description.length}/300 {t('reminder.characters')}</p>
            </div>

            {/* Frequency — One Time or Repeat */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('reminder.frequency')}</label>

              {/* Level 1: One Time | Repeat */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                {[
                  { key: false, label: t('reminder.one_time') },
                  { key: true,  label: t('reminder.repeat_label') },
                ].map(({ key, label }) => (
                  <button key={String(key)} type="button"
                    onClick={() => setIsRepeat(key)}
                    className={`py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1 ${
                      isRepeat === key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'
                    }`}>
                    {isRepeat === key && <Check size={13} strokeWidth={3} />}
                    {label}
                  </button>
                ))}
              </div>

              {/* Level 2: Yearly | Monthly | Weekly — only when Repeat */}
              {isRepeat && (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'yearly',  label: t('reminder.yearly') },
                    { key: 'monthly', label: t('reminder.monthly') },
                    { key: 'weekly',  label: t('reminder.weekly') },
                  ].map(({ key, label }) => (
                    <button key={key} type="button" onClick={() => {
                      setRepeatFrequency(key);
                      const max = key === 'weekly' ? 6 : key === 'monthly' ? 29 : 90;
                      if (remindCustomDays !== '' && parseInt(remindCustomDays) > max) setRemindCustomDays(String(max));
                    }}
                      className={`py-2.5 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-1 ${
                        repeatFrequency === key ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 border border-slate-200'
                      }`}>
                      {repeatFrequency === key && <Check size={12} strokeWidth={3} />}
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reminder Time */}
            <div className="bg-white rounded-xl overflow-hidden border border-slate-100">
              <div className="flex items-center justify-between p-4">
                <div>
                  <span className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {t('reminder.reminder_time') || 'Reminder Time'}
                  </span>
                  {!useCustomTime && (() => {
                    const def = getSettingsDefaultTime(settings);
                    const label = `${def.hour}:${String(def.minute).padStart(2, '0')} ${def.period}`;
                    return (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {(t('reminder.using_default_time') || 'Using default · {time}').replace('{time}', label)}
                      </p>
                    );
                  })()}
                </div>
                <button type="button" data-testid="custom-time-toggle"
                  onClick={() => {
                    if (!useCustomTime) {
                      const def = getSettingsDefaultTime(settings);
                      setReminderHour(def.hour);
                      setReminderMinute(def.minute);
                      setReminderPeriod(def.period);
                    }
                    setUseCustomTime(p => !p);
                  }}
                  className={`w-12 h-7 rounded-full transition-colors relative flex-shrink-0 ${useCustomTime ? 'bg-slate-900' : 'bg-slate-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${useCustomTime ? 'translate-x-6' : 'translate-x-1'}`}></div>
                </button>
              </div>

              {useCustomTime && (
                <div className="px-4 pb-4">
                  <TimePicker
                    hour={reminderHour} minute={reminderMinute} period={reminderPeriod}
                    testIdPrefix="reminder"
                    onChange={({ hour, minute, period }) => {
                      setReminderHour(hour);
                      setReminderMinute(minute);
                      setReminderPeriod(period);
                    }}
                  />
                </div>
              )}
            </div>

            {/* Remind Before — multi-select: same day, 1 day before, custom */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('reminder.remind_before')}</label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {/* Same day chip */}
                <button type="button" onClick={() => setRemindSameDay(p => !p)}
                  data-testid="remind-before-same"
                  className={`py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1 ${remindSameDay ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>
                  {remindSameDay && <Check size={13} strokeWidth={3} />}
                  {t('reminder.same_day')}
                </button>
                {/* 1 day before chip */}
                <button type="button" onClick={() => setRemindOneDayBefore(p => !p)}
                  data-testid="remind-before-1day"
                  className={`py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1 ${remindOneDayBefore ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>
                  {remindOneDayBefore && <Check size={13} strokeWidth={3} />}
                  {t('reminder.one_day')}
                </button>
                {/* Custom chip */}
                <button type="button" onClick={() => setRemindCustom(p => !p)}
                  data-testid="remind-before-custom"
                  className={`py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1 ${remindCustom ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>
                  {remindCustom && <Check size={13} strokeWidth={3} />}
                  {t('reminder.custom_days')}
                </button>
              </div>
              {remindCustom && (
                <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-slate-200">
                  <input
                    type="number"
                    min="1"
                    max={repeatFrequency === 'weekly' ? 6 : repeatFrequency === 'monthly' ? 29 : 90}
                    value={remindCustomDays}
                    placeholder="—"
                    data-testid="remind-before-days-input"
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') { setRemindCustomDays(''); return; }
                      const max = repeatFrequency === 'weekly' ? 6 : repeatFrequency === 'monthly' ? 29 : 90;
                      const n = parseInt(raw);
                      if (!isNaN(n)) setRemindCustomDays(String(Math.min(max, Math.max(1, n))));
                    }}
                    className="w-20 bg-slate-50 border border-transparent focus:border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-center"
                  />
                  <span className="text-sm text-slate-600">{t('reminder.days_before')}</span>
                </div>
              )}
            </div>

            {/* Group Assignment */}
            {groups.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('reminder.assign_group')}</label>
                <select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)} data-testid="group-select"
                  className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors">
                  <option value="">{t('reminder.none')}</option>
                  {groups.map((group) => (<option key={group.id} value={group.id}>{group.name}</option>))}
                </select>
              </div>
            )}

            {/* Error Display */}
            {saveError && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl" data-testid="save-reminder-error">{saveError}</div>
            )}

            {/* Submit */}
            <button type="submit" disabled={saving} data-testid="add-reminder-submit"
              className="w-full bg-slate-900 text-white py-4 rounded-full font-bold text-lg active:scale-[0.98] transition-transform mt-6 disabled:opacity-50">
              {saving ? t('reminder.saving') : t('reminder.save')}
            </button>
            </form>
        </motion.div>
      </div>

      <BottomNav />
    </div>
  );
};

export default AddReminderPage;
